// The /create "Video" row's sheet. Marble Run has to be picked before the
// cover is made: the race is simulated first and decides which voice sings
// when, so it can't be added in /studio afterwards. Everything else about
// the video (captions, trim, backgrounds) happens in /studio once the cover
// exists.
//
// The sheet leads with a live preview of the race in the chosen look, the
// cover's own voices as the marbles (an illustration: the real race is
// simulated on the server and decides who sings when). The template payload
// is the one MarbleRunTemplates.js (CreateModal) sent; the server renders the
// race with the lyrics burnt in.
import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Crown, Dices } from "lucide-react";

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface MarbleRunLook {
  marble: string;
  background: string;
  stars: boolean;
  font: string;
}

const MARBLES = [
  "#f39c12",
  "#e74c3c",
  "#2ecc71",
  "#3498db",
  "#9b59b6",
  "#1abc9c",
  "#f1c40f",
  "#fd79a8",
];
const BACKGROUNDS = [
  "#1a1a2e",
  "#0f3460",
  "#16213e",
  "#2d132c",
  "#0a1931",
  "#1f4068",
  "#162447",
  "#1a1a40",
];
const FONTS = [
  "Bangers",
  "Anton",
  "Archivo Black",
  "Cherry Bomb One",
  "Poppins",
];

// Named looks: one tap sets the whole race's colours, font and sky.
const THEMES: { name: string; look: MarbleRunLook }[] = [
  {
    name: "Midnight",
    look: {
      background: "#1a1a2e",
      marble: "#f39c12",
      stars: true,
      font: "Bangers",
    },
  },
  {
    name: "Neon",
    look: {
      background: "#2d132c",
      marble: "#fd79a8",
      stars: true,
      font: "Cherry Bomb One",
    },
  },
  {
    name: "Ocean",
    look: {
      background: "#0f3460",
      marble: "#1abc9c",
      stars: false,
      font: "Poppins",
    },
  },
  {
    name: "Royal",
    look: {
      background: "#16213e",
      marble: "#9b59b6",
      stars: true,
      font: "Archivo Black",
    },
  },
  {
    name: "Lava",
    look: {
      background: "#1a1a40",
      marble: "#e74c3c",
      stars: false,
      font: "Anton",
    },
  },
  {
    name: "Gold",
    look: {
      background: "#0a1931",
      marble: "#f1c40f",
      stars: true,
      font: "Bangers",
    },
  },
];

export const DEFAULT_MARBLE_RUN: MarbleRunLook = THEMES[0].look;

const pick = <T,>(list: T[]) => list[Math.floor(Math.random() * list.length)];

const rgba = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 1)`;
};

// Same shape as MarbleRunTemplates.js handleSaveConfiguration.
export function marbleRunTemplate(look: MarbleRunLook) {
  return {
    flairs: [
      {
        type: "lyrics",
        properties: {
          border: "true",
          dancing: "still",
          effects: "glow",
          font: look.font,
          primary: "single word",
          textColor: rgba("#FFFFFF"),
          transition: "blur",
          centerX: "true",
        },
        layout: { rotate: 0, offset: [0, 0.53], scale: 1 },
      },
      {
        type: "battle",
        properties: { dancing: "dancing", effects: "glow", sticker: "true" },
        layout: {
          rotate: 0,
          offset: [-0.2758945098704537, 0.7605932284209688],
          scale: 1,
        },
      },
      {
        type: "versus",
        properties: { dancing: "still", fade: "none" },
        layout: { rotate: 0, offset: [0.2898148, 0.03125], scale: 1 },
      },
    ],
    game: {
      type: "marble_run",
      properties: {
        clouds: "false",
        color: rgba(look.marble),
        progressbar: "false",
        winner: "0",
      },
      flairs: [],
    },
    background: [
      {
        type: "color",
        properties: { color: rgba(look.background), effect: "deep" },
      },
      ...(look.stars
        ? [{ type: "animation", properties: { type: "stars", properties: {} } }]
        : []),
    ],
  };
}

// ── the live preview ───────────────────────────────────────────────────────

// Zigzag ramps down a 9:16 frame, as fractions of it.
const TRACK: [number, number][] = [
  [0.14, 0.12],
  [0.86, 0.24],
  [0.14, 0.38],
  [0.86, 0.52],
  [0.14, 0.66],
  [0.86, 0.8],
];
const SEGS = TRACK.slice(1).map((p, i) => {
  const a = TRACK[i];
  return { a, b: p, len: Math.hypot(p[0] - a[0], p[1] - a[1]) };
});
const TRACK_LEN = SEGS.reduce((n, s) => n + s.len, 0);

function pointAt(s: number): [number, number] {
  let d = Math.max(0, Math.min(1, s)) * TRACK_LEN;
  for (const seg of SEGS) {
    if (d <= seg.len) {
      const f = d / seg.len;
      return [
        seg.a[0] + (seg.b[0] - seg.a[0]) * f,
        seg.a[1] + (seg.b[1] - seg.a[1]) * f,
      ];
    }
    d -= seg.len;
  }
  return TRACK[TRACK.length - 1];
}

const RACE_SECS = 7;

function RacePreview({ look, voices }: { look: MarbleRunLook; voices: any[] }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const lookRef = useRef(look);
  lookRef.current = look;
  const [photos, setPhotos] = useState<(HTMLImageElement | null)[]>([]);

  // Voice photos for the marbles. Drawn only (never read back), so a
  // cross-origin image is fine here.
  const key = voices.map((v) => v?._id).join(",");
  useEffect(() => {
    let alive = true;
    Promise.all(
      voices.slice(0, 4).map(
        (v) =>
          new Promise<HTMLImageElement | null>((done) => {
            if (!v?.image?.url) return done(null);
            const img = new Image();
            img.onload = () => done(img);
            img.onerror = () => done(null);
            img.src = v.image.url;
          }),
      ),
    ).then((list) => alive && setPhotos(list));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const W = canvas.width,
      H = canvas.height;
    const n = Math.max(2, Math.min(4, voices.length || 2));
    // Each marble's pace wobbles on its own, so the lead changes hands.
    const pace = Array.from({ length: n }, (_, i) => ({
      base: 1 - i * 0.012,
      gap: i * 0.035,
      w: 0.8 + i * 0.45,
      p: i * 2.1,
    }));
    const stars = Array.from({ length: 40 }, (_, i) => [
      ((i * 97) % 100) / 100,
      ((i * 57) % 100) / 100,
      0.5 + ((i * 13) % 10) / 10,
    ]);
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let raf = 0;
    const start = performance.now();

    const draw = (now: number) => {
      const L = lookRef.current;
      const t = reduce ? 3.2 : ((now - start) / 1000) % (RACE_SECS + 1.2);
      // Sky.
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, L.background);
      g.addColorStop(1, "#05030a");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
      if (L.stars) {
        ctx.fillStyle = "#FFFFFF";
        for (const [x, y, r] of stars) {
          ctx.globalAlpha = 0.35 + 0.5 * Math.abs(Math.sin(t * 1.3 + x * 9));
          ctx.beginPath();
          ctx.arc(x * W, y * H, r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      // Ramps.
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = W * 0.035;
      ctx.strokeStyle = "rgba(255,255,255,0.16)";
      ctx.beginPath();
      TRACK.forEach(([x, y], i) =>
        i ? ctx.lineTo(x * W, y * H) : ctx.moveTo(x * W, y * H),
      );
      ctx.stroke();
      ctx.lineWidth = W * 0.012;
      ctx.strokeStyle = L.marble;
      ctx.stroke();

      // Marbles: progress along the track, a little apart sideways.
      const prog = pace.map(({ base, gap, w, p }) => {
        const s = (t / RACE_SECS) * base - gap + Math.sin(t * w + p) * 0.05;
        return Math.max(0, Math.min(1, s));
      });
      const lead = prog.indexOf(Math.max(...prog));
      const r = W * 0.085;
      prog
        .map((s, i) => ({ s, i }))
        .sort((a, b) => a.s - b.s)
        .forEach(({ s, i }) => {
          const [x, y] = pointAt(s);
          const cx = x * W + (i - (n - 1) / 2) * r * 0.9,
            cy = y * H - r * 0.9;
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
          ctx.fillStyle = i === lead ? "#D1FE17" : L.marble;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.clip();
          const img = photos[i];
          if (img) ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
          else {
            ctx.fillStyle = L.marble;
            ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
          }
          ctx.restore();
          if (i === lead) {
            // The crown on whoever leads: they're the one singing.
            ctx.fillStyle = "#D1FE17";
            ctx.beginPath();
            const k = r * 0.55,
              bx = cx - k,
              by = cy - r - k * 0.35;
            ctx.moveTo(bx, by);
            ctx.lineTo(bx + k * 0.4, by - k * 0.8);
            ctx.lineTo(bx + k, by - k * 0.25);
            ctx.lineTo(bx + k * 1.6, by - k * 0.8);
            ctx.lineTo(bx + k * 2, by);
            ctx.closePath();
            ctx.fill();
          }
        });

      // A lyric in the chosen font.
      const word = ["la", "la", "LAND", "ED", "IN", "BROOK", "LYN"][
        Math.floor(t * 2.2) % 7
      ];
      ctx.font = `${Math.round(W * 0.16)}px "${L.font}", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineWidth = W * 0.02;
      ctx.strokeStyle = "rgba(0,0,0,0.6)";
      ctx.shadowColor = L.marble;
      ctx.shadowBlur = W * 0.06;
      ctx.strokeText(word, W / 2, H * 0.92);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText(word, W / 2, H * 0.92);
      ctx.shadowBlur = 0;

      if (!reduce) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [photos, voices.length]);

  return (
    <canvas
      ref={ref}
      width={360}
      height={640}
      className="tw-h-[300px] tw-w-[169px] tw-flex-shrink-0 tw-rounded-[22px] tw-bg-jam-ink tw-shadow-[0_18px_40px_-16px_rgba(46,15,82,0.6)] md:tw-h-[340px] md:tw-w-[191px]"
      aria-label="Preview of the Marble Run"
    />
  );
}

// ── the sheet ─────────────────────────────────────────────────────────────

const Swatches = ({
  colors,
  value,
  onPick,
}: {
  colors: string[];
  value: string;
  onPick: (c: string) => void;
}) => (
  <div className="tw-flex tw-flex-wrap tw-gap-2">
    {colors.map((c) => (
      <button
        key={c}
        type="button"
        aria-label={c}
        onClick={() => onPick(c)}
        style={{ background: c }}
        className={`tw-flex tw-h-9 tw-w-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-text-white ${value === c ? "tw-ring-[3px] tw-ring-jam-purple tw-ring-offset-2" : ""}`}
      >
        {value === c && <Check size={14} strokeWidth={3.5} />}
      </button>
    ))}
  </div>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="tw-text-[13px] tw-font-extrabold tw-text-jam-ink">
    {children}
  </div>
);

export default function VideoOptions({
  look,
  onChange,
  voices = [],
}: {
  look: MarbleRunLook;
  onChange: (look: MarbleRunLook) => void;
  voices?: any[];
}) {
  const set = (patch: Partial<MarbleRunLook>) =>
    onChange({ ...look, ...patch });
  const [custom, setCustom] = useState(false);
  // The race's lyric fonts, so the chips and the preview show them.
  useEffect(() => {
    const id = "marble-run-google-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${FONTS.map((f) => f.replace(/ /g, "+")).join("&family=")}&display=swap`;
    document.head.appendChild(link);
  }, []);
  const themeOn = (t: MarbleRunLook) =>
    t.background === look.background &&
    t.marble === look.marble &&
    t.stars === look.stars &&
    t.font === look.font;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5 tw-text-jam-ink">
      <div className="tw-flex tw-items-center tw-gap-4">
        <RacePreview look={look} voices={voices} />
        <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-3">
          <div className="jam-display tw-text-[24px] tw-font-extrabold tw-leading-[1.05] tw-tracking-[-0.03em]">
            Your voices race. Whoever leads, sings.
          </div>
          <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-jam-muted">
            Lead changes become voice changes, with the lyrics on screen. Ready
            to post on TikTok and Reels.
          </p>
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-extrabold tw-text-jam-purple">
            <Crown size={14} strokeWidth={2.75} />
            The crown shows who&apos;s singing
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2">
        <Label>Pick a look</Label>
        <div className="tw-grid tw-grid-cols-3 tw-gap-2">
          {THEMES.map((t) => {
            const on = themeOn(t.look);
            return (
              <button
                key={t.name}
                type="button"
                onClick={() => onChange(t.look)}
                className={`tw-relative tw-flex tw-h-[72px] tw-cursor-pointer tw-flex-col tw-justify-end tw-overflow-hidden tw-rounded-[18px] tw-border-0 tw-p-2.5 tw-text-left ${on ? "tw-ring-[3px] tw-ring-jam-purple tw-ring-offset-2" : ""}`}
                style={{
                  background: `linear-gradient(160deg, ${t.look.background}, #05030a)`,
                }}
              >
                <span className="tw-absolute tw-right-2.5 tw-top-2.5 tw-flex tw-gap-1">
                  <span
                    className="tw-h-3.5 tw-w-3.5 tw-rounded-full"
                    style={{ background: t.look.marble }}
                  />
                  <span className="tw-h-3.5 tw-w-3.5 tw-rounded-full tw-bg-jam-lime" />
                </span>
                <span
                  className="tw-text-[15px] tw-text-white"
                  style={{ fontFamily: t.look.font }}
                >
                  {t.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <button
          type="button"
          onClick={() => setCustom((v) => !v)}
          aria-expanded={custom}
          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-extrabold tw-text-jam-purple"
        >
          Customise
          <ChevronDown
            size={16}
            strokeWidth={2.75}
            className={`tw-transition-transform ${custom ? "tw-rotate-180" : ""}`}
          />
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              marble: pick(MARBLES),
              background: pick(BACKGROUNDS),
              stars: Math.random() > 0.4,
              font: pick(FONTS),
            })
          }
          className="tw-flex tw-h-10 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-4 tw-text-[13px] tw-font-extrabold tw-text-jam-purple"
        >
          <Dices size={16} strokeWidth={2.5} />
          Surprise me
        </button>
      </div>

      {custom && (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-rounded-[22px] tw-bg-jam-lav tw-p-4">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Label>Marbles and track</Label>
            <Swatches
              colors={MARBLES}
              value={look.marble}
              onPick={(marble) => set({ marble })}
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Label>Sky</Label>
            <Swatches
              colors={BACKGROUNDS}
              value={look.background}
              onPick={(background) => set({ background })}
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Label>Lyrics font</Label>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {FONTS.map((font) => (
                <button
                  key={font}
                  type="button"
                  onClick={() => set({ font })}
                  style={{ fontFamily: font }}
                  className={`tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-4 tw-text-[15px] ${look.font === font ? "tw-bg-jam-ink tw-text-white" : "tw-bg-white tw-text-jam-ink"}`}
                >
                  {font}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={look.stars}
            onClick={() => set({ stars: !look.stars })}
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-[16px] tw-border-0 tw-bg-white tw-p-3 tw-pl-4 tw-text-left"
          >
            <span className="tw-text-[14px] tw-font-extrabold tw-text-jam-ink">
              Stars in the sky
            </span>
            <span
              className={`tw-flex tw-h-7 tw-w-12 tw-items-center tw-rounded-full tw-p-1 ${look.stars ? "tw-bg-jam-purple" : "tw-bg-jam-line"}`}
            >
              <span
                className={`tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-transition-transform ${look.stars ? "tw-translate-x-5" : ""}`}
              />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
